import React from 'react';
import { FormElementOption, TIRE_WEAR_OPTION_LABELS, type TireWearOptionLabel } from '@packages/models/api';
import { Icon } from '../../shared/icon';
import { Image, View, Pressable, StyleSheet, ImageSourcePropType } from 'react-native';
import { Typography } from '../../shared/typography';
import colors from '@packages/core/styles/colors';

const IMAGE_BY_LABEL: Record<TireWearOptionLabel, ImageSourcePropType> = {
    [TIRE_WEAR_OPTION_LABELS.Center]: require('../../assets/images/TireWearCenter.png'),
    [TIRE_WEAR_OPTION_LABELS.Cupped]: require('../../assets/images/TireWearCupped.png'),
    [TIRE_WEAR_OPTION_LABELS.Diagonal]: require('../../assets/images/TireWearDiagonal.png'),
    [TIRE_WEAR_OPTION_LABELS.EvenAndEqualWear]: require('../../assets/images/TireWearEven.png'),
    [TIRE_WEAR_OPTION_LABELS.OuterEdge]: require('../../assets/images/TireWearOuter.png'),
    [TIRE_WEAR_OPTION_LABELS.SingleEdge]: require('../../assets/images/TireWearSingle.png'),
};

const OPTION_WIDTH = 214;
const OPTION_HEIGHT = 228;
const GAP = 24;

const MAX_THREE_COL_WIDTH = OPTION_WIDTH * 3 + GAP * 2;

const MIN_OPTION_WIDTH = 150;
const OPTION_ASPECT_RATIO = OPTION_WIDTH / OPTION_HEIGHT;
const MIN_OPTION_HEIGHT = MIN_OPTION_WIDTH / OPTION_ASPECT_RATIO;

interface TireWearRadioProps {
    disabled?: boolean;
    onChange(option: FormElementOption): void;
    options: FormElementOption[];
    value: string;
}

export function TireWearRadio({ disabled = false, onChange, options, value }: TireWearRadioProps) {
    return (
        <View style={styles.radioGroup}>
            {options.map((option) => {
                const selected = option.optionId === value;

                return (
                    <Pressable
                        key={`radio-option-${option.optionId}`}
                        accessibilityLabel={option.optionLabel}
                        accessibilityRole="radio"
                        accessibilityState={{ selected, disabled }}
                        disabled={disabled}
                        onPress={() => onChange(option)}
                        style={styles.optionContainer}
                    >
                        <View style={styles.radioCard}>
                            <View style={styles.labelContainer}>
                                <Icon
                                    size={24}
                                    name={selected ? 'radio-marked' : 'radio-unmarked'}
                                    color={selected ? 'blueOne' : 'grayFive'}
                                    accessible={false}
                                />
                                <Typography>{option.optionLabel}</Typography>
                            </View>

                            <View style={styles.imageContainer}>
                                <Image
                                    accessibilityLabel={option.optionLabel}
                                    accessible={false}
                                    source={IMAGE_BY_LABEL[option.optionLabel as TireWearOptionLabel]}
                                    style={styles.optionImage}
                                />
                            </View>
                        </View>
                    </Pressable>
                );
            })}
        </View>
    );
}

const styles = StyleSheet.create({
    radioGroup: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: GAP,
        marginBottom: 8,
        maxWidth: MAX_THREE_COL_WIDTH,
    },
    optionContainer: {
        aspectRatio: OPTION_ASPECT_RATIO,
        flexBasis: OPTION_WIDTH,
        flexGrow: 0,
        flexShrink: 1,
        minHeight: MIN_OPTION_HEIGHT,
        minWidth: MIN_OPTION_WIDTH,
        width: OPTION_WIDTH,
    },
    radioCard: {
        borderColor: colors.grayFive,
        borderRadius: 4,
        borderWidth: 1,
        gap: 8,
        padding: 8,
        height: '100%',
        width: '100%',
    },
    labelContainer: {
        gap: 8,
        flexDirection: 'row',
        alignItems: 'center',
    },
    imageContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    optionImage: {
        width: '100%',
        height: '100%',
        resizeMode: 'contain',
    },
});
